import config from '@config'
import blickSchemaOrgLogo from '@assets/images/logo_schemaorg.png'
import { PageMetadata } from '@hooks/usePageMetadata'
import { FilteredArticleSchemaImage } from '@components/ArticleSchema'
import { ListAuthor } from '@widgets/Author/types'

interface ImageProps {
  element: any
  customRatio?: string
  isMainImage?: boolean
}

export type BreadcrumbSchema = Record<string, any>

export type HomeSchema = Record<string, any>

export type ArticleSchema = Record<string, any>

export type VideoSchema = Record<string, any>

export interface Schema {
  breadcrumb?: BreadcrumbSchema
  home?: HomeSchema
  article?: ArticleSchema
  video?: VideoSchema
}

export interface TeaserImageProps {
  teaserImage: {
    src: string
    alt?: string
    crops?: any
  }
}

export interface ArticleSchemaImage {
  url?: string
  alt?: string
  width?: number
  height?: number
}

const {
  backend: { baseUrl, imageUrl },
  video: { accountId },
} = config

const imageConfig: Record<string, any> = {
  ratios: {
    '1_1': {
      name: '1_1',
      width: 1,
      height: 1,
    },
    '4_3': {
      name: '4_3',
      width: 4,
      height: 3,
    },
    '16_9': {
      name: '16_9',
      width: 16,
      height: 9,
    },
    free: {
      name: 'free',
      width: 'auto',
      height: 'auto',
    },
  },
  sizes: {
    XS: 'XS',
    S: 'S',
    M: 'M',
    L: 'L',
    XL: 'XL',
    XLL: 'XLL',
    XXL: 'XXL',
    retina: {
      XS: 'XSR',
      S: 'SR',
      M: 'MR',
      L: 'LR',
      XL: 'XLR',
      XLL: 'XLL',
      XXL: 'XXLR',
    },
  },
  widths: {
    XS: 112,
    S: 300,
    M: 375,
    L: 500,
    XL: 1000,
    XLL: 1200,
    XXL: 2000,
  },
}

const _createCommonParams = ({
  size = '',
  ratio,
  isRetina,
  pixelDensity,
}: {
  size: any
  ratio: Record<string, any>
  isRetina: boolean
  pixelDensity: number
}) => {
  const { name: ratioName = '' } = ratio
  const selectedSize =
    size[isRetina ? 'retina' : 'default'] || (isRetina ? size + 'R' : size)

  // For native apps only
  // Fix for blurry images of high density devices
  // As the Akamai Image Manager only supports integers (no floats)
  // for the imdensity parameter, we have to round here
  if (pixelDensity) {
    return (
      'imwidth=' +
      imageConfig.widths[size] +
      '&imdensity=' +
      Math.round(pixelDensity) +
      '&ratio=' +
      ratioName.toUpperCase()
    )
  }

  // get default or retina width
  const width =
    imageConfig.widths[selectedSize] ||
    imageConfig.widths[selectedSize.slice(0, -1)] * 2

  return 'imwidth=' + width + '&ratio=' + ratioName.toUpperCase()
}

const _createURL = (
  src: string,
  cropParams: string,
  commonParams: string,
  isExternal: boolean
) => {
  if (isExternal) {
    return src
  }
  const path = `${src}?${commonParams}${cropParams}`

  // Disabling token generation for the sake of performance
  // const tokenParam =
  //   edgeAuth.options.tokenName + '=' + edgeAuth.generateURLToken(path)

  return `${imageUrl}${path}`
}

export const createCropParams = ({
  crops,
  ratio,
}: {
  crops: Record<string, any>
  ratio: Record<string, any>
}) => {
  const crop = crops[ratio.name] || crops || {} // free crops can come in from API as crops: { free: {}} or crops: {}
  const { x = 0, y = 0, width, height } = crop

  return width
    ? '&x=' + x + '&y=' + y + '&width=' + width + '&height=' + height
    : ''
}

export const createSrcArray = ({
  src,
  crops = {},
  options = { ratio: {} },
  isExternal,
}: {
  src: string
  crops: Record<string, any>
  options: { ratio: Record<string, any> }
  isExternal: boolean
}) => {
  const cropParams = createCropParams({
    crops,
    ...options,
  })
  const defaultCommonParams = _createCommonParams(options as any)
  const retinaCommonParams = _createCommonParams({
    ...options,
    isRetina: true,
  } as any)

  return [
    _createURL(src, cropParams, defaultCommonParams, isExternal),
    _createURL(src, cropParams, retinaCommonParams, isExternal),
  ]
}

const createResponsiveImageSrcSet = ({
  src,
  crops = {},
  options = {},
  isExternal = false,
}: {
  src: string
  crops: Record<string, any>
  options: Record<string, any>
  isExternal: boolean
}) => {
  if (src) {
    const [defaultImageURL] = createSrcArray({
      src,
      crops,
      options,
      isExternal,
    } as any)

    if (options.asArrayOfURISource) {
      return [
        {
          uri: defaultImageURL,
          scale: 1,
        },
      ]
    }

    return defaultImageURL
  }
}

const SCHEMAORG_IMAGE_RATIOS = [
  imageConfig.ratios['16_9'],
  imageConfig.ratios['4_3'],
  imageConfig.ratios['1_1'],
]

const getImageProps = ({
  element,
  customRatio,
  isMainImage,
}: ImageProps): ArticleSchemaImage[] => {
  const src = element?.image?.src

  // We want to show only the main image (due to Google AMP rules)
  if (!src) {
    return [{} as ArticleSchemaImage]
  }

  const cSrc = Array.isArray(src) ? src[0] : src
  const alt = element?.image?.alt
  const crops = element?.image?.crops
  const ratios: any = isMainImage
    ? SCHEMAORG_IMAGE_RATIOS.map((ratio) => ratio.name)
    : [customRatio || 'free']

  return ratios.map((ratio: any) => {
    const width = crops?.[ratio]?.width
    const height = crops?.[ratio]?.height
    return {
      url:
        createResponsiveImageSrcSet({
          src: cSrc,
          crops: crops,
          options: {
            ratio: { name: ratio },
            size: isMainImage ? imageConfig.sizes.XXL : imageConfig.sizes.XL,
          },
        } as any) ?? '',
      alt,
      width,
      height,
    }
  })
}

const getTeaserImageProps = ({
  teaserImage,
}: TeaserImageProps): Array<ArticleSchemaImage> => {
  const { src, alt, crops } = teaserImage

  return (SCHEMAORG_IMAGE_RATIOS as any).map((ratio: any) => {
    const crop = crops?.[ratio.name] || crops || {} // free crops can come in from API as crops: { free: {}} or crops: {}
    let { width, height } = crop

    // If width is > then 1200 -> downscale the width to 1200 and calculate the height accordingly
    if (width && height) {
      if (width > imageConfig.widths.XLL) {
        height = Math.round(height / (width / imageConfig.widths.XLL))
        width = imageConfig.widths.XLL
      }
    }

    return {
      url:
        createResponsiveImageSrcSet({
          src: src,
          crops: crops,
          options: {
            ratio: { name: ratio.name },
            size: imageConfig.sizes.XLL,
          },
        } as any) ?? '',
      alt,
      width,
      height,
    }
  })
}

const defaultAuthor = 'Blick.ch'

const blickFBPage = 'https://www.facebook.com/blick'
const blickTWPage = 'https://twitter.com/blickch'
const blickInstaPage = 'https://instagram.com/blick/'
const blickWikipedia = 'https://de.wikipedia.org/wiki/Blick.ch'

const blickLogoInfo = {
  src: blickSchemaOrgLogo.replace('/_next/static', baseUrl),
  width: 137,
  height: 60,
}

const createListItem = (position: number, url: string, name: string) => ({
  '@type': 'ListItem',
  position,
  item: {
    '@id': `${baseUrl}${url}`,
    name,
  },
})

const createStep = (text: string, image?: string) => ({
  '@type': 'HowToStep',
  text,
  image,
})

const createNutritionInformation = (calories?: string) => ({
  nutrition: {
    '@type': 'NutritionInformation',
    calories,
  },
})

const createBreadcrumbList = (
  listItems: ReturnType<typeof createListItem>[]
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: listItems,
})

const createAuthor = (name = defaultAuthor) => ({
  '@type': 'Person',
  name,
})

const createImage = (
  url: string,
  width: number | undefined,
  height: number | undefined
) => ({
  '@type': 'ImageObject',
  url,
  width,
  height,
})

const defaultLogo = createImage(
  blickLogoInfo.src,
  blickLogoInfo.width,
  blickLogoInfo.height
)

const createPublisher = () => ({
  '@type': 'Organization',
  name: 'Blick',
  logo: defaultLogo,
})

const createVideoObjectSchema = (props: any) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    ...props.properties,
  }
}

const createBreadcrumbDefaultSchema = (
  pageMetadata: PageMetadata
): BreadcrumbSchema | undefined => {
  const { type, breadcrumbs, htmlTitle } = pageMetadata

  if (
    type === 'article' &&
    breadcrumbs &&
    breadcrumbs.length > 0 &&
    htmlTitle
  ) {
    const totalBreadcrumbs = breadcrumbs.length
    const listItems = breadcrumbs.map((crumb, index) =>
      createListItem(
        index + 1,
        crumb.href,
        totalBreadcrumbs === index + 1 && htmlTitle ? htmlTitle : crumb.title
      )
    )
    return createBreadcrumbList(listItems)
  }
}

const createHomeDefaultSchema = (
  pageMetadata: PageMetadata
): HomeSchema | undefined => {
  const { url, context } = pageMetadata
  if (context === 'home') {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': 'https://www.blick.ch#organisation',
      name: 'Blick',
      logo: blickLogoInfo.src,
      description:
        'News aus Sport, Wirtschaft, Politik und Unterhaltung. Nachrichten aus der Schweiz und der Welt mit exklusiven Interviews, Videos und Berichten.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Dufourstrasse 23',
        addressLocality: 'Zürich Seefeld',
        addressRegion: 'ZH',
        postalCode: '8008',
        addressCountry: 'Schweiz',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        telephone: '+41 44 259 62 62',
        email: 'redaktion@blick.ch',
      },
      sameAs: [blickFBPage, blickTWPage, blickInstaPage, blickWikipedia],
      url: url,
    }
  }
}

const createVideoDefaultSchema = (
  pageMetadata: PageMetadata
): VideoSchema | undefined => {
  const { hasVideo, teaser, thirdPartyId } = pageMetadata

  const videoDuration = teaser?.video?.duration

  const isLiveBroadcast = videoDuration === -1

  if (
    hasVideo &&
    teaser &&
    thirdPartyId &&
    videoDuration &&
    (videoDuration > 0 || isLiveBroadcast)
  ) {
    const {
      url,
      metaTitle,
      metaDescription,
      publishedDate,
      videoTranscript,
      liveVideoStartTime,
      liveVideoEndTime,
    } = pageMetadata

    return {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: metaTitle,
      description: metaDescription,
      uploadDate: publishedDate,
      // ISO 8601-format: PT = Period and Time (always at the beginning), S = seconds
      duration: `PT${videoDuration}S`,
      contentUrl: `https://players.brightcove.net/${accountId}/default_default/index.html?videoId=${thirdPartyId}`,
      embedUrl: `${baseUrl}${url}`,
      transcript: videoTranscript || '',

      ...(isLiveBroadcast
        ? {
            duration: undefined, // we need to remove duration for livestream video (duration -1 is invalid)
            publication: {
              '@type': 'BroadcastEvent',
              isLiveBroadcast: true,
              startDate: liveVideoStartTime,
              endDate: liveVideoEndTime,
            },
          }
        : {}),
      ...(teaser?.image
        ? {
            thumbnailUrl: getTeaserImageProps({
              teaserImage: teaser.image as any,
            })
              .filter(({ url }) => !!url)
              .map(({ url }) => url),
          }
        : {}),
    }
  }
}

const generateInitialSchema = (pageMetadata: PageMetadata): Schema => ({
  breadcrumb: createBreadcrumbDefaultSchema(pageMetadata),
  home: createHomeDefaultSchema(pageMetadata),
  video: createVideoDefaultSchema(pageMetadata),
})

const mergeArticleSchema = (
  schema: Schema,
  updatedArticleSchemaProps: Partial<ArticleSchema>
): Schema => {
  const newSchema = { ...schema }

  newSchema.article = { ...newSchema.article, ...updatedArticleSchemaProps }

  return newSchema
}

const getImageSet = (image?: TeaserImageProps['teaserImage']) => {
  if (image) {
    const imageSet = getTeaserImageProps({
      teaserImage: image,
    })

    const filteredImageSet = imageSet.filter(
      ({ url }) => !!url
    ) as FilteredArticleSchemaImage[]

    return filteredImageSet.map((image) =>
      createImage(image.url, image.width, image.height)
    )
  }
  return defaultLogo
}

const getParsedAuthors = (authors?: ListAuthor[]) => {
  const parsedAuthors: string[] = []
  authors?.forEach((author) =>
    parsedAuthors.push(...author.name.replace(/ (und|et) /, ',').split(','))
  )
  const isValid = parsedAuthors.every(
    (author) => author.trim()?.[0] === author.trim()?.[0].toUpperCase()
  )
  return isValid ? parsedAuthors : []
}

export {
  createBreadcrumbList,
  createListItem,
  createStep,
  createAuthor,
  createImage,
  createPublisher,
  createNutritionInformation,
  createVideoObjectSchema,
  getImageProps,
  generateInitialSchema,
  createBreadcrumbDefaultSchema,
  createHomeDefaultSchema,
  createVideoDefaultSchema,
  mergeArticleSchema,
  getTeaserImageProps,
  getImageSet,
  getParsedAuthors,
  blickFBPage,
  blickTWPage,
  blickInstaPage,
  defaultLogo,
}
